import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import {
  mapReportPrograms,
  tenYearsList,
} from '../../../ReportsBeta/NewReport/NewReportContent';
import { ReportReceipt } from '../../../ReportsBeta/OldReport';
import { Program } from '../../../Reports/NewReportDialog';
import TrueImpactDropdown from 'shared/design-system/components/TrueImpactDropdown';
import { useClient } from 'shared/apollo';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import { getReportByIdApi, ReportAPIResponse } from '../../../ReportsBeta/api';
import { requestInit } from 'shared/auth/hooks/useRequestState';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';

interface PeekAnswersContentProps {
  currentProgram: Program;
  programs: ReturnType<typeof mapReportPrograms>;
}

const StyledDiv = styled.div`
  .dropdown-wrapper {
    width: 100%;
    display: flex;
    justify-content: space-between;
    .MuiSelect-select {
    }
  }
  .empty-text {
    margin-top: ${SPACERS.medium};
  }
`;

const PeekAnswersContent: React.FC<PeekAnswersContentProps> = ({
  programs,
  currentProgram,
}) => {
  const client = useClient();
  const [requestState, makeRequest, setRequestState] = useGraphQLRequest<{
    allReports: ReportAPIResponse[];
  }>();
  const [selected, setSelected] = useState<{
    year: number | undefined;
    program: Program | undefined;
  }>({
    year: undefined,
    program: programs.programs.find(x => x.Id === currentProgram.Id),
  });

  useEffect(() => {
    if (!client) return;
    const programId = selected.program?.Id;
    const year = selected.year;
    if (!programId || !year) return;
    const reportId = programs.reportPrograms[programId]?.[year]?.reportId;
    if (!reportId) return;
    makeRequest(getReportByIdApi(client, reportId));
  }, [selected]);

  const handleProgramChange = useCallback(
    (program: Program) => {
      setSelected({
        program,
        year: undefined,
      });
      setRequestState(requestInit());
    },
    [setSelected],
  );
  const handleYearChange = useCallback(
    (year: number) => {
      setSelected(s => ({
        ...s,
        year,
      }));
    },
    [setSelected],
  );

  const report = requestState.data?.allReports[0];

  return (
    <StyledDiv>
      <div className="dropdown-wrapper">
        <TrueImpactDropdown
          value={selected.program}
          options={programs.programs}
          getId={p => p.Id}
          getLabel={p => p.name}
          native={true}
          onChange={handleProgramChange}
          onClear={undefined}
          getItemDisability={undefined}
          name="Please select your program"
          className="dropdown-select"
        />
        <TrueImpactDropdown
          disabled={!selected.program}
          value={selected.year}
          options={tenYearsList}
          getId={y => y}
          getLabel={y => y}
          getItemDisability={y => {
            const programId = selected.program?.Id;
            if (!programId) return true;
            const report = programs.reportPrograms[programId]?.[y];
            if (!report || !report.reportId) return true;
            return report.status === 'Draft';
          }}
          native={true}
          onChange={handleYearChange}
          onClear={undefined}
          name="Please select your report year"
          className="dropdown-select"
        />
      </div>
      {requestState.type === 'REQUEST_START' ? (
        <TrueImpactLoading />
      ) : report ? (
        <ReportReceipt
          programName={report.program.name}
          report={{ ...report, id: report.dbId }}
          showReportStatus={true}
        />
      ) : (
        <TrueImpactText
          align="center"
          variant="body-bold"
          className="empty-text"
        >
          Please make sure both program and year are selected.
        </TrueImpactText>
      )}
    </StyledDiv>
  );
};

export default PeekAnswersContent;
